import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { useNavigate } from 'react-router-dom';

const PURPLE = '#7C3AED';
const GREEN = '#69F0AE';
const BG_DARK = '#0A0E17';
const CARD_BG = '#121829';
const BORDER = 'rgba(255,255,255,0.08)';
const TEXT_PRIMARY = '#E8ECF4';
const TEXT_SECONDARY = '#8892A8';

export default function OnboardingSuccessPage() {
  const navigate = useNavigate();

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ bgcolor: BG_DARK, px: 2, py: 4 }}
    >
      <Box maxWidth={520} width="100%">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            sx={{
              color: PURPLE,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
            }}
          >
            Cadence
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: TEXT_SECONDARY, mt: 0.5, fontSize: '0.95rem' }}
          >
            Welcome to Cadence
          </Typography>
        </Box>

        {/* Success Card */}
        <Card
          sx={{
            bgcolor: CARD_BG,
            border: `1px solid ${BORDER}`,
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: GREEN, mb: 2 }}
            />
            <Typography
              variant="h5"
              sx={{ color: TEXT_PRIMARY, fontWeight: 600, mb: 1.5 }}
            >
              Your account has been created
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: TEXT_SECONDARY, mb: 0.5 }}
            >
              Check your email to set your password and get started.
            </Typography>
          </CardContent>
        </Card>

        {/* What happens next */}
        <Card
          sx={{
            bgcolor: CARD_BG,
            border: `1px solid ${BORDER}`,
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h6"
              sx={{ color: TEXT_PRIMARY, fontWeight: 600, mb: 3 }}
            >
              What happens next
            </Typography>

            <Box display="flex" alignItems="flex-start" gap={2} mb={2.5}>
              <EmailOutlinedIcon sx={{ color: PURPLE, fontSize: 22, mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography variant="body1" sx={{ color: TEXT_PRIMARY, fontWeight: 500, fontSize: '0.95rem' }}>
                  Your 14-day trial has started
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem' }}>
                  Full access to all features, no restrictions during your trial.
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={2} mb={2.5}>
              <CalendarTodayOutlinedIcon sx={{ color: PURPLE, fontSize: 22, mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography variant="body1" sx={{ color: TEXT_PRIMARY, fontWeight: 500, fontSize: '0.95rem' }}>
                  We'll send you a daily planning guide by email
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem' }}>
                  Tips and best practices to get the most from your operating rhythm.
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={2}>
              <RocketLaunchOutlinedIcon sx={{ color: PURPLE, fontSize: 22, mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography variant="body1" sx={{ color: TEXT_PRIMARY, fontWeight: 500, fontSize: '0.95rem' }}>
                  Start by clicking "Start My Day" on your dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem' }}>
                  Your AI-powered daily plan will be generated in seconds.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* CTA */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => navigate('/')}
          sx={{
            bgcolor: PURPLE,
            color: '#fff',
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.5,
            '&:hover': {
              bgcolor: '#6D28D9',
            },
          }}
        >
          Go to login
        </Button>

        <Typography
          variant="body2"
          sx={{
            color: TEXT_SECONDARY,
            textAlign: 'center',
            mt: 3,
            fontSize: '0.82rem',
          }}
        >
          If you don't receive an email within a few minutes, check your spam folder.
        </Typography>
      </Box>
    </Box>
  );
}
